import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Col, Form, Input, Row, Typography } from 'antd';
import { useDisclosure } from '@/hooks';
import {
  ChangeWorkOrderItemDiscountRequest,
  WorkOrderItemType,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useChangeWorkOrderItemDiscount } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { FloatingFormItem, InputDecimal } from '@/components/_common';
import WoekOrderItemsDiscountModal from './WoekOrderItemsDiscountModal';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { AxiosError } from 'axios';

const { Paragraph } = Typography;

interface WorkOrderItemsTotalDiscountsProps {
  complementId: number;
  totalDiscountPart?: number;
  totalDiscountService?: number;
  maxDiscountPart?: number;
  maxDiscountService?: number;
  refetch?: () => void;
}

function WorkOrderItemsTotalDiscounts({
  complementId,
  totalDiscountPart,
  totalDiscountService,
  maxDiscountPart,
  maxDiscountService,
  refetch,
}: WorkOrderItemsTotalDiscountsProps) {
  const intl = useIntl();
  const [form] = Form.useForm<ChangeWorkOrderItemDiscountRequest>();
  const [formDiscount] = Form.useForm<{
    partDiscount: number;
    serviceDiscount: number;
  }>();

  const {
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const isClosed = complement?.status === WorkOrderComplementStatus.Closed;

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [itemType, setItemType] = useState<WorkOrderItemType>();

  const [modalVisible, modalAction] = useDisclosure(false, {
    onClose: () => setErrorMessage(null),
  });

  const mutation = useChangeWorkOrderItemDiscount();

  useEffect(() => {
    formDiscount.setFieldsValue({
      partDiscount: totalDiscountPart || 0,
      serviceDiscount: totalDiscountService || 0,
    });
  }, [totalDiscountPart, totalDiscountService]);

  const handleSubmit = async () => {
    setErrorMessage(null);

    try {
      const values = await form.validateFields();

      if (!itemType) {
        throw new Error('Tipo do item inválido');
      }

      const obj: ChangeWorkOrderItemDiscountRequest = {
        ...values,
        complementId,
        type: itemType,
      };

      await mutation.mutateAsync(obj);

      if (refetch) {
        refetch();
      }

      modalAction.close();
      setItemType(undefined);
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;

      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      }
    }
  };

  return (
    <>
      <Form form={formDiscount}>
        <Row gutter={24}>
          <Col span={24} md={12}>
            <Paragraph strong style={{ marginBottom: '16px' }}>
              <FormattedMessage id="workOrders.details.items.totalDiscountPart" />
            </Paragraph>
            <Input.Group compact className="floating-form-item-group">
              <FloatingFormItem
                name="partDiscount"
                label={`${intl.formatMessage({
                  id: 'commons.entity.value',
                })} (R$)`}
                style={{ flex: '1' }}
              >
                <InputDecimal fractions={2} disabled />
              </FloatingFormItem>

              <Button
                type="primary"
                size="large"
                onClick={() => {
                  setItemType(WorkOrderItemType.Part);
                  modalAction.open();
                }}
                disabled={isClosed}
              >
                <FormattedMessage id="commons.entity.edit" />
              </Button>
            </Input.Group>
          </Col>

          <Col span={24} md={12}>
            <Paragraph strong style={{ marginBottom: '16px' }}>
              <FormattedMessage id="workOrders.details.items.totalDiscountService" />
            </Paragraph>
            <Input.Group compact className="floating-form-item-group">
              <FloatingFormItem
                name="serviceDiscount"
                label={`${intl.formatMessage({
                  id: 'commons.entity.value',
                })} (R$)`}
                style={{ flex: '1' }}
              >
                <InputDecimal fractions={2} disabled />
              </FloatingFormItem>

              <Button
                type="primary"
                size="large"
                onClick={() => {
                  setItemType(WorkOrderItemType.Service);
                  modalAction.open();
                }}
                disabled={isClosed}
              >
                <FormattedMessage id="commons.entity.edit" />
              </Button>
            </Input.Group>
          </Col>
        </Row>
      </Form>

      <WoekOrderItemsDiscountModal
        open={modalVisible}
        onCancel={modalAction.close}
        itemType={itemType}
        form={form}
        onOk={handleSubmit}
        confirmLoading={mutation.isLoading}
        errorMessage={errorMessage}
        maxDiscount={
          itemType === WorkOrderItemType.Part
            ? maxDiscountPart || 0
            : maxDiscountService || 0
        }
      />
    </>
  );
}

export default WorkOrderItemsTotalDiscounts;
